import React, { useState, useEffect } from 'react';
import { FormControl } from 'baseui/form-control';
import { Input } from 'baseui/input';
import { Select } from 'baseui/select';
import { ParagraphSmall } from 'baseui/typography';
import { Package, Trash2, Save, X } from 'lucide-react';
import { useCreateProduct, useUpdateProduct, useDeleteProduct, type Product } from '../hooks/useProducts';
import { businessHasCarnetConfigured, type Business } from '../hooks/useBusinessAccessType';
import { ReadOnlyFence } from '../components/ReadOnlyFence';
import { useFdCan } from '../hooks/useFdCan';
import { ProductConfigForm } from '../components/product/ProductConfigForm';
import { productToConfig, configToProduct, type ProductConfigForm as ConfigForm } from '../components/product/productConfig';

interface ProductDetailPanelProps {
  product: Product | null;
  mode: 'create' | 'edit';
  businessId: string | null;
  businesses?: Business[];
  isAdmin?: boolean;
  onClose: () => void;
  onSaved?: () => void;
}

export const ProductDetailPanel: React.FC<ProductDetailPanelProps> = ({
  product,
  mode,
  businessId,
  businesses = [],
  isAdmin = false,
  onClose,
  onSaved,
}) => {
  // SAM-1312 (U19): the Front Desk and the estate group roles only view Business; no editing, no Delete, no Save.
  const canEdit = useFdCan('business', 'edit');
  const createProduct = useCreateProduct();
  const updateProduct = useUpdateProduct();
  const deleteProduct = useDeleteProduct();

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    payment_gateway: '' as string,
    business_id: '' as string,
  });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [config, setConfig] = useState<ConfigForm>(() => productToConfig(null));

  useEffect(() => {
    if (mode === 'edit' && product) {
      setFormData({
        name: product.name || '',
        description: product.description || '',
        price: product.price != null ? String(product.price) : '',
        payment_gateway: product.payment_gateway || '',
        business_id: product.business_id || '',
      });
      setConfig(productToConfig(product));
    } else {
      setFormData({
        name: '',
        description: '',
        price: '',
        payment_gateway: '',
        business_id: businessId || '',
      });
      setConfig(productToConfig(null));
    }
    setShowDeleteConfirm(false);
  }, [product, mode, businessId]);

  const handleSave = () => {
    const data: Partial<Product> = {
      name: formData.name,
      description: formData.description || undefined,
      price: formData.price ? Number(formData.price) : undefined,
      payment_gateway: (formData.payment_gateway as any) || undefined,
      ...configToProduct(config),
    };

    if (mode === 'create') {
      data.business_id = formData.business_id || businessId || undefined;
      createProduct.mutate(data, { onSuccess: () => { onSaved?.(); onClose(); } });
    } else if (product) {
      updateProduct.mutate({ id: product.id, data }, { onSuccess: () => { onSaved?.(); onClose(); } });
    }
  };

  const handleDelete = () => {
    if (!product) return;
    deleteProduct.mutate(product.id, { onSuccess: () => { onSaved?.(); onClose(); } });
  };

  const isSaving = createProduct.isPending || updateProduct.isPending;
  const isDeleting = deleteProduct.isPending;
  const isFormValid = formData.name.trim().length > 0 && (mode === 'edit' || !!formData.business_id);
  const title = mode === 'create' ? 'New Product' : product?.name || 'Product';

  const businessOptions = businesses.map((b) => ({ label: b.name, id: b.id }));
  const selectPopover = {
    Popover: { props: { overrides: { Body: { style: { zIndex: 100001 } } } } },
    Dropdown: { style: { zIndex: 100001 } },
  };

  // Cardnet is only allowed when the destination business has both
  // merchant_id and terminal_id set on its settings JSONB.
  const selectedBusiness = businesses.find((b) => b.id === formData.business_id);
  const carnetEnabled = businessHasCarnetConfigured(selectedBusiness?.settings);
  const gatewayOptions = [
    { label: 'Portal', id: 'portal' },
    {
      label: carnetEnabled ? 'Cardnet' : 'Cardnet (not configured)',
      id: 'carnet',
      disabled: !carnetEnabled,
    },
  ];

  // If the business loses Cardnet config while a product is set to carnet,
  // surface it in the helper text so the user notices before saving.
  const showCarnetMissingHint = formData.payment_gateway === 'carnet' && !carnetEnabled && !!formData.business_id;

  return (
    <div className="bk-detail-pane-inner">
      <header className="bk-detail-header">
        <div className="bk-detail-header-left">
          <div className="bz-avatar">
            <Package size={20} color="#fff" />
          </div>
          <div>
            <div className="bk-detail-title">{title}</div>
            <div className="bk-detail-sub">
              {mode === 'create' ? 'Create a new product' : `ID: ${product?.id}`}
            </div>
          </div>
        </div>
        <button className="bk-icon-btn" onClick={onClose} aria-label="Close">
          <X size={16} />
        </button>
      </header>

      <div className="bk-detail-scroll">
        <ReadOnlyFence section="business">
        <div className="bz-tab-panel">
          <section className="bz-section">
            <div className="bz-section-title">Product Information</div>

            <FormControl label="Name">
              <Input
                value={formData.name}
                onChange={(e) => setFormData((p) => ({ ...p, name: (e.target as HTMLInputElement).value }))}
                placeholder="Product name"
                size="compact"
                disabled={!canEdit}
              />
            </FormControl>

            <FormControl label="Description">
              <Input
                value={formData.description}
                onChange={(e) => setFormData((p) => ({ ...p, description: (e.target as HTMLInputElement).value }))}
                placeholder="Product description (optional)"
                size="compact"
                disabled={!canEdit}
              />
            </FormControl>

            <div className="bz-grid-2">
              <FormControl label="Price (USD)">
                <Input
                  value={formData.price}
                  onChange={(e) => setFormData((p) => ({ ...p, price: (e.target as HTMLInputElement).value }))}
                  placeholder="0.00"
                  type="number"
                  size="compact"
                  startEnhancer="$"
                  disabled={!canEdit}
                />
              </FormControl>

              <FormControl
                label="Payment Gateway"
                caption={showCarnetMissingHint
                  ? 'Cardnet is not configured for this business — set Merchant ID and Terminal ID in Settings.'
                  : undefined}
              >
                <Select
                  options={gatewayOptions}
                  value={gatewayOptions.filter((o) => o.id === formData.payment_gateway)}
                  onChange={({ value }) => setFormData((p) => ({ ...p, payment_gateway: (value[0]?.id as string) || '' }))}
                  placeholder="Select gateway"
                  size="compact"
                  clearable={canEdit}
                  disabled={!canEdit}
                  overrides={selectPopover}
                />
              </FormControl>
            </div>

            {(mode === 'create' || isAdmin) && (
              <FormControl label="Business">
                <Select
                  options={businessOptions}
                  value={businessOptions.filter((o) => o.id === formData.business_id)}
                  onChange={({ value }) => setFormData((p) => ({ ...p, business_id: (value[0]?.id as string) || '' }))}
                  placeholder="Select business"
                  size="compact"
                  clearable={false}
                  searchable
                  disabled={mode === 'edit' || !canEdit}
                  overrides={selectPopover}
                />
              </FormControl>
            )}
          </section>

          {/* Availability / conditions / tiers / images / categories */}
          <ProductConfigForm value={config} onChange={setConfig} />

          {mode === 'edit' && product && canEdit && (
            <section className="bz-section">
              <div className="bz-section-title">Danger Zone</div>
              {!showDeleteConfirm ? (
                <button
                  type="button"
                  className="bz-danger-btn"
                  onClick={() => setShowDeleteConfirm(true)}
                  disabled={isDeleting}
                >
                  <Trash2 size={14} />
                  Delete Product
                </button>
              ) : (
                <div className="bz-danger-confirm">
                  <ParagraphSmall margin="0 0 12px" color="#374151" style={{ fontSize: '13px', fontWeight: 500 }}>
                    This action is permanent and cannot be undone.
                  </ParagraphSmall>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      type="button"
                      className="bz-secondary-btn"
                      onClick={() => setShowDeleteConfirm(false)}
                      disabled={isDeleting}
                      style={{ flex: 1 }}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="bz-danger-btn"
                      onClick={handleDelete}
                      disabled={isDeleting}
                      style={{ flex: 1 }}
                    >
                      <Trash2 size={14} />
                      {isDeleting ? 'Deleting...' : 'Confirm Delete'}
                    </button>
                  </div>
                </div>
              )}
            </section>
          )}
        </div>
        </ReadOnlyFence>
      </div>

      {/* SAM-1320 (U42): a view-only role has nothing to cancel; the header's close button leaves the panel */}
      {canEdit && (
      <footer className="bk-detail-footer">
        <button type="button" className="bz-secondary-btn" onClick={onClose} disabled={isSaving}>
          <X size={14} />
          Cancel
        </button>
        {canEdit && (
          <button
            type="button"
            className="bz-primary-btn"
            onClick={handleSave}
            disabled={!isFormValid || isSaving}
          >
            <Save size={14} />
            {isSaving ? 'Saving...' : mode === 'create' ? 'Create Product' : 'Save Changes'}
          </button>
        )}
      </footer>
      )}
    </div>
  );
};
